import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, Phone, ArrowUpRight } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const WHATSAPP_URL = 'https://wa.me/447305703314';
const EMAIL = 'munumhamza81@gmail.com';
const PHONE_DISPLAY = '07305 703314';
const PHONE_TEL = '+447305703314';

// Official WhatsApp glyph (green)
const WhatsAppLogo = ({ size = 28, className = '' }) => (
  <svg
    viewBox="0 0 32 32"
    width={size}
    height={size}
    aria-hidden="true"
    className={className}
  >
    <path
      fill="#25D366"
      d="M16.001 0C7.165 0 .001 7.164.001 16c0 2.82.738 5.575 2.137 7.998L0 32l8.225-2.16A15.93 15.93 0 0 0 16 32c8.836 0 16-7.163 16-16S24.836 0 16.001 0Z"
    />
    <path
      fill="#FFFFFF"
      d="M23.487 19.62c-.41-.205-2.428-1.196-2.804-1.333-.377-.137-.65-.205-.923.205-.273.41-1.058 1.333-1.298 1.606-.238.273-.478.308-.888.103-.41-.205-1.732-.638-3.298-2.034-1.22-1.087-2.042-2.428-2.282-2.838-.238-.41-.025-.632.18-.836.184-.184.41-.478.615-.717.205-.24.273-.41.41-.683.137-.273.069-.512-.034-.717-.103-.205-.923-2.228-1.265-3.05-.333-.798-.673-.69-.923-.704-.238-.013-.512-.016-.785-.016-.273 0-.717.102-1.093.512-.376.41-1.435 1.401-1.435 3.417 0 2.016 1.469 3.965 1.674 4.238.205.273 2.892 4.416 7.005 6.193 2.448 1.057 3.404 1.146 4.629.963.745-.112 2.428-.992 2.77-1.95.342-.957.342-1.778.24-1.95-.102-.171-.376-.273-.785-.478Z"
    />
  </svg>
);

const ContactCard = ({ icon, eyebrow, value, action, href, delay }) => (
  <motion.a
    href={href}
    target={href.startsWith('http') ? '_blank' : undefined}
    rel={href.startsWith('http') ? 'noopener noreferrer' : undefined}
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.3 }}
    transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
    whileHover={{ y: -4 }}
    className="group block rounded-2xl bg-neutral-50 hover:bg-neutral-100 border border-neutral-200 p-6 md:p-7 transition-colors"
  >
    <div className="flex items-start justify-between gap-3">
      <div className="w-12 h-12 rounded-xl bg-white border border-neutral-200 flex items-center justify-center shrink-0">
        {icon}
      </div>
      <ArrowUpRight
        size={18}
        className="text-neutral-400 group-hover:text-neutral-900 transition-colors"
      />
    </div>
    <div className="mt-5 text-[11px] tracking-[0.22em] uppercase text-neutral-500">
      {eyebrow}
    </div>
    <div className="mt-2 font-display text-[16px] md:text-[18px] font-semibold text-neutral-900 break-all leading-snug">
      {value}
    </div>
    <div className="mt-1 text-[13px] text-neutral-500">{action}</div>
  </motion.a>
);

const Contact = () => {
  const { toast } = useToast();
  const [form, setForm] = useState({ firstName: '', lastName: '', email: '', phone: '', message: '' });
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.firstName || !form.lastName || !form.email) {
      toast({ title: 'Please fill in the required fields', description: 'First name, last name and email are required.' });
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`https://formsubmit.co/ajax/${EMAIL}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          name: `${form.firstName} ${form.lastName}`,
          email: form.email,
          phone: form.phone || 'Not provided',
          message: form.message || '(No message)',
          _subject: `New enquiry from ${form.firstName} ${form.lastName} — Bio & Chem with Hamza`,
          _template: 'table',
          _captcha: 'false',
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && (data.success === 'true' || data.success === true)) {
        toast({ title: 'Message sent', description: "Thanks — I'll get back to you shortly." });
        setForm({ firstName: '', lastName: '', email: '', phone: '', message: '' });
      } else {
        throw new Error(data.message || 'Submission failed');
      }
    } catch (err) {
      toast({
        title: "Couldn't send message",
        description: `Please email ${EMAIL} directly or try again.`,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-20 md:py-28 bg-white">
      <div className="max-w-6xl mx-auto px-6 md:px-10">
        {/* Heading */}
        <div className="max-w-3xl">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.5 }}
            transition={{ duration: 0.6 }}
            className="text-[12px] tracking-[0.22em] uppercase text-neutral-500"
          >
            Get in touch
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.8 }}
            className="mt-4 font-display text-[40px] md:text-[56px] tracking-tight font-semibold text-neutral-900"
          >
            Contact Us Now
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="mt-5 text-[15px] md:text-[16px] leading-[1.7] text-neutral-600 max-w-xl"
          >
            Reach out for tutoring enquiries, admissions information, or to learn more about
            sessions. The fastest way to get in touch is WhatsApp.
          </motion.p>
        </div>

        {/* Quick contact cards */}
        <div className="mt-10 md:mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
          <ContactCard
            icon={<WhatsAppLogo size={26} />}
            eyebrow="WhatsApp"
            value={PHONE_DISPLAY}
            action="Tap to chat or call"
            href={WHATSAPP_URL}
            delay={0}
          />
          <ContactCard
            icon={<Mail size={22} strokeWidth={1.75} className="text-neutral-900" />}
            eyebrow="Email"
            value={EMAIL}
            action="Tap to send an email"
            href={`mailto:${EMAIL}`}
            delay={0.1}
          />
          <ContactCard
            icon={<Phone size={22} strokeWidth={1.75} className="text-neutral-900" />}
            eyebrow="Phone"
            value={PHONE_DISPLAY}
            action="Tap to call"
            href={`tel:${PHONE_TEL}`}
            delay={0.2}
          />
        </div>

        {/* Form */}
        <div className="mt-16 md:mt-20 grid md:grid-cols-[1fr_320px] gap-10 md:gap-16 items-start">
          <div>
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.5 }}
              transition={{ duration: 0.6 }}
              className="text-[12px] tracking-[0.22em] uppercase text-neutral-500"
            >
              Or send a message
            </motion.div>

            <motion.form
              onSubmit={submit}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.8, delay: 0.15 }}
              className="mt-6 max-w-xl space-y-6"
            >
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <label className="form-label">First name *</label>
                  <input className="form-input" value={form.firstName} onChange={update('firstName')} />
                </div>
                <div>
                  <label className="form-label">Last name *</label>
                  <input className="form-input" value={form.lastName} onChange={update('lastName')} />
                </div>
              </div>
              <div>
                <label className="form-label">Email *</label>
                <input type="email" className="form-input" value={form.email} onChange={update('email')} />
              </div>
              <div>
                <label className="form-label">Phone</label>
                <input className="form-input" value={form.phone} onChange={update('phone')} />
              </div>
              <div>
                <label className="form-label">Message</label>
                <textarea rows={3} className="form-input resize-none" value={form.message} onChange={update('message')} />
              </div>

              <motion.button
                type="submit"
                disabled={submitting}
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.99 }}
                className="pill-btn w-full bg-neutral-900 text-white py-4 text-[14px] font-medium disabled:opacity-60"
              >
                {submitting ? 'Sending...' : 'Submit'}
              </motion.button>
            </motion.form>
          </div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-[14px] text-neutral-600 leading-[1.7] md:pt-12"
          >
            Prefer a quick chat? Tap the WhatsApp button above — replies usually within a few hours.
            Otherwise, drop me a message via the form and I&apos;ll get back to you shortly.
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
